import { Code } from '../../../util/types';
import { QuestionnaireSearchData } from './buildQuestionnaireSearchQuery';
import { CodingSystem } from '@lifeomic/fhir-constants';
import { DateTime } from 'luxon';
import {
  hasCsvExtension,
  isCsvMime,
} from '../../../../../util/knowledgeBaseCitation';

type CreationCtx = {
  questionnaireData: QuestionnaireSearchData;
};

export const PRIMARY_SURVEY_ID_SYSTEM =
  'http://lifeomic.com/fhir/primary-survey-id';

const CLEERLY_CODE_SYSTEM = 'http://lifeomic.com/fhir/cleerly-test-code';
const BOSTON_HEART_CODE_SYSTEM =
  'http://lifeomic.com/fhir/boston-heart-test-code';

export const PAGE_NUMBER_EXTENSION_URL =
  'http://lifeomic.com/fhir/fountain-life-page-number';

export const DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM =
  'http://lifeomic.com/fhir/document-extraction-v2-projection';

export const isDocumentExtractionV2Resource = (
  resource?: fhir.Resource | null,
): boolean =>
  (
    resource as { identifier?: Array<{ system?: string }> } | undefined
  )?.identifier?.some(
    identifier =>
      identifier.system === DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
  ) ?? false;

export abstract class Attachment {
  protected _resourceType: string | undefined;
  protected _resourceId: string | undefined;
  protected _display: string | undefined;
  protected _date: string | undefined;

  constructor(public resource: fhir.Resource) {
    this._resourceType = resource.resourceType;
    this._resourceId = resource.id;
  }

  get identifier() {
    return `${this._resourceType}/${this._resourceId}`;
  }

  get rawDisplay() {
    return this._display ?? this._resourceType;
  }

  get display() {
    let text = this._display ?? this._resourceType;
    if (this.displayDate) {
      let date = this.displayDate;
      date = date.replace(' ', " '");
      text = `${text} ${date}`;
    }
    return text;
  }

  get displayDate() {
    if (!this._date) {
      return undefined;
    } else {
      const dateTime = DateTime.fromISO(this._date);
      return dateTime.isValid ? dateTime.toFormat('LLL yy') : undefined;
    }
  }

  static from: (ctx: CreationCtx, resource: fhir.Resource) => Attachment;
}

export class UnknownAttachment extends Attachment {}

export class DocumentReferenceAttachment extends Attachment {
  constructor(public resource: fhir.DocumentReference) {
    super(resource);

    this._display = resource.type?.text;
    this._date = resource.created;
  }

  isImage() {
    // naiive check for now, can we get the content type from document reference?
    return (
      this._display?.includes('.png') ||
      this._display?.includes('.jpg') ||
      this._display?.includes('.jpeg')
    );
  }

  /**
   * True when the underlying file should be rendered as a CSV table preview
   * instead of being shoved through an iframe (browsers download CSVs from
   * iframes, which is the wrong UX inside an executive summary).
   *
   * Probe the FHIR `content[].attachment` first since that's the canonical
   * shape; fall back to `type.text` for cases where the contentType wasn't
   * persisted but the title carries the `.csv` filename.
   */
  isCsv() {
    const attachment = this.resource.content?.[0]?.attachment;
    return Boolean(
      isCsvMime(attachment?.contentType) ||
        hasCsvExtension(attachment?.title) ||
        hasCsvExtension(attachment?.url) ||
        hasCsvExtension(this._display),
    );
  }
}

export class ImageReferenceAttachment extends Attachment {
  constructor(public resource: fhir.DocumentReference) {
    super(resource);

    this._display = resource.type?.text;
    this._date = resource.created;
  }
}

export class ObservationAttachment extends Attachment {
  public isBiomarker = false;
  constructor(public resource: fhir.Observation) {
    super(resource);
    this._date = resource.effectiveDateTime;

    // consider using related.target.reference to get the display name (e.g QuestionnaireResponse/123)
    const loincCodeDisplay = resource.code?.coding?.find(
      code => code.system === CodingSystem.LOINC,
    )?.display;
    if (loincCodeDisplay) {
      this._display = loincCodeDisplay;
    } else {
      const snomedCodeDisplay = resource.code?.coding?.find(
        code => code.system === CodingSystem.SNOMED,
      )?.display;
      this._display = snomedCodeDisplay;
    }

    this.isBiomarker = this._display !== this._resourceType;
  }

  /**
   * Returns the primary code for the observation.
   *
   * This is _very_ implementation specific with regards to medical-results-service
   * A better implementation would likely try to search for all codes
   *
   * Or else medical-results-service should be better designed to support returning
   * results for equivalent codes
   *
   * Implementation is roughly in priority:
   * 1. Cleerly
   * 2. Boston Heart
   * 3. LOINC
   * 4. SNOMED
   * 5. First code not matching any of the above
   */
  get primaryCode(): Code | null {
    const cleerlyCodeValue = this.resource.code?.coding?.find(
      code => code.system === CLEERLY_CODE_SYSTEM,
    )?.code;
    if (cleerlyCodeValue) {
      return { key: cleerlyCodeValue, system: CLEERLY_CODE_SYSTEM };
    }

    const loincCodeValue = this.resource.code?.coding?.find(
      code => code.system === CodingSystem.LOINC,
    )?.code;
    if (loincCodeValue) {
      return { key: loincCodeValue, system: CodingSystem.LOINC };
    }

    const bostonHeartCodeValue = this.resource.code?.coding?.find(
      code => code.system === BOSTON_HEART_CODE_SYSTEM,
    )?.code;
    if (bostonHeartCodeValue) {
      return { key: bostonHeartCodeValue, system: BOSTON_HEART_CODE_SYSTEM };
    }

    const snomedCodeValue = this.resource.code?.coding?.find(
      code => code.system === CodingSystem.SNOMED,
    )?.code;
    if (snomedCodeValue) {
      return { key: snomedCodeValue, system: CodingSystem.SNOMED };
    }

    const firstCode = this.resource.code?.coding?.[0];

    return firstCode ? { key: firstCode.code, system: firstCode.system } : null;
  }
}

export const getConditionCodeDisplay = (resource: fhir.Condition): string => {
  const code = resource.code;
  if (code?.text) return code.text;
  const codingWithDisplay = code?.coding?.find(coding => coding.display);
  if (codingWithDisplay?.display) return codingWithDisplay.display;
  const noteText = (resource as { note?: Array<{ text?: string }> }).note?.[0]
    ?.text;
  if (noteText) return noteText;
  if (code?.coding?.[0]?.code) return code.coding[0].code;
  return 'Condition';
};

export class ConditionAttachment extends Attachment {
  constructor(public resource: fhir.Condition) {
    super(resource);

    this._display = getConditionCodeDisplay(resource);
    this._date = resource.onsetDateTime ?? resource.onsetPeriod?.start;
  }
}

export const getMedicationRequestDisplay = (
  resource: fhir.MedicationRequest,
): string => {
  return (
    resource.medicationCodeableConcept?.text ||
    resource.medicationCodeableConcept?.coding?.find(coding => coding.display)
      ?.display ||
    resource.medicationCodeableConcept?.coding?.[0]?.code ||
    resource.medicationReference?.display ||
    resource.medicationReference?.reference ||
    'Medication Request'
  );
};

export class MedicationRequestAttachment extends Attachment {
  constructor(public resource: fhir.MedicationRequest) {
    super(resource);

    this._display = getMedicationRequestDisplay(resource);
    this._date = resource.authoredOn;
  }
}

export class QuestionnaireResponseAttachment extends Attachment {
  public primarySurveyId?: string;

  constructor(ctx: CreationCtx, public resource: fhir.QuestionnaireResponse) {
    super(resource);
    this._date = resource.authored;

    this.primarySurveyId = resource.meta?.tag?.find(
      tag => tag.system === PRIMARY_SURVEY_ID_SYSTEM,
    )?.code;

    this._display = ctx.questionnaireData.hits.hits.find(
      survey =>
        survey._source.meta?.tag?.find(
          tag => tag.system === PRIMARY_SURVEY_ID_SYSTEM,
        )?.code === this.primarySurveyId,
    )?._source.title;
  }
}

export class PatientAttachment extends Attachment {
  constructor(public resource: fhir.Patient) {
    super(resource);

    if (!resource.name?.length) return;

    const preferred =
      resource.name.find(n => n.use === 'nickname') ?? resource.name[0]!;

    const firstName = preferred.given?.[0] ?? preferred.text?.split(' ')[0];
    const lastName = preferred.family ?? '';

    this._display = `${firstName ?? ''} ${lastName}`.trim();
  }
}

export class CarePlanAttachment extends Attachment {}

const CommunicationCodeMappings: Record<string, string> = {
  'Elation VisitNote': 'Visit Note',
  'Elation NonVisitNote': 'Note',
};
export class CommunicationAttachment extends Attachment {
  constructor(public resource: fhir.Communication) {
    super(resource);
    this._date = resource.sent;

    const titleCode = resource.meta?.tag?.find(
      tag => tag.system === 'http://lifeomic.com/fhir/source-type',
    )?.code;

    if (titleCode && CommunicationCodeMappings[titleCode]) {
      this._display = CommunicationCodeMappings[titleCode];
    }
  }
}

Attachment.from = (ctx: CreationCtx, resource: fhir.Resource): Attachment => {
  switch (resource.resourceType) {
    case 'DocumentReference':
      return new DocumentReferenceAttachment(
        resource as fhir.DocumentReference,
      );
    case 'Observation':
      return new ObservationAttachment(resource as fhir.Observation);
    case 'Condition':
      return new ConditionAttachment(resource as fhir.Condition);
    case 'MedicationRequest':
      return new MedicationRequestAttachment(
        resource as fhir.MedicationRequest,
      );
    case 'Patient':
      return new PatientAttachment(resource as fhir.Patient);
    case 'QuestionnaireResponse':
      return new QuestionnaireResponseAttachment(
        ctx,
        resource as fhir.QuestionnaireResponse,
      );
    case 'CarePlan':
      return new CarePlanAttachment(resource);
    case 'Communication':
      return new CommunicationAttachment(resource as fhir.Communication);
    default:
      return new UnknownAttachment(resource);
  }
};

export class TemporaryAttachment extends DocumentReferenceAttachment {
  constructor(resource: fhir.Reference, name: string) {
    super({
      resourceType: 'DocumentReference',
      content: [],
      indexed: 'asdf',
      status: 'current',
      id: resource.reference?.split('/').at(1),
      type: {
        coding: [],
        text: name,
      },
    });
    this._display = name;
  }
}

export type ReferenceOrResource = fhir.Reference | fhir.Resource;

export const isFhirReference = (
  reference: ReferenceOrResource,
): reference is fhir.Reference => {
  return (reference as fhir.Reference).reference !== undefined;
};

export const isFhirResource = (
  reference: ReferenceOrResource,
): reference is fhir.Resource => {
  return (reference as fhir.Resource).resourceType !== undefined;
};

export const isDocumentReference = (
  reference: ReferenceOrResource,
): reference is fhir.DocumentReference => {
  return (
    (reference as fhir.DocumentReference).resourceType === 'DocumentReference'
  );
};

export const isObservation = (
  reference: ReferenceOrResource,
): reference is fhir.Observation => {
  return (reference as fhir.Observation).resourceType === 'Observation';
};

export const isCondition = (
  reference: ReferenceOrResource,
): reference is fhir.Condition => {
  return (reference as fhir.Condition).resourceType === 'Condition';
};

export const isMedicationRequest = (
  reference: ReferenceOrResource,
): reference is fhir.MedicationRequest => {
  return (
    (reference as fhir.MedicationRequest).resourceType === 'MedicationRequest'
  );
};

export const isPractitionerType = (reference: ReferenceOrResource): boolean => {
  if (isFhirReference(reference)) {
    return reference.reference?.startsWith('Practitioner') ?? false;
  }

  return reference.resourceType?.startsWith('Practitioner') ?? false;
};

export const getCitationIdPrefix = (
  reference: ReferenceOrResource,
): 'dr' | 'o' | 'c' | 'mr' | undefined => {
  return isDocumentReference(reference) ||
    (isFhirReference(reference) &&
      reference.reference?.startsWith('DocumentReference/'))
    ? 'dr'
    : isObservation(reference)
    ? 'o'
    : isCondition(reference) ||
      (isFhirReference(reference) &&
        reference.reference?.startsWith('Condition/'))
    ? 'c'
    : isMedicationRequest(reference) ||
      (isFhirReference(reference) &&
        reference.reference?.startsWith('MedicationRequest/'))
    ? 'mr'
    : undefined;
};
